import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import warning from "@/assets/warning.svg";
import { Copy, FileEdit, CheckCircle2, Archive, Loader2, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import React from "react";
import { useToast } from "@/components/ui/use-toast";
import { ErrorType, WorkoutPlanView } from "@/types";
import {
  useAddWorkoutMutation,
  useDeleteWorkoutMutation,
  useLazyGetWorkoutByIdQuery,
  useAddWorkoutDayMutation,
  useAddExerciseInWorkoutMutation,
  useUpdateWorkoutMutation,
  useVerifyWorkoutCreationMutation,
  useUpdateWorkoutStatusMutation,
} from "@/services/workoutApi";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

export function DataTableRowActions({
  access,
  row,
  data,
  refetch,
  handleEdit,
}: {
  access: string;
  row: number;
  data: WorkoutPlanView & { id: number };
  refetch?: any;
  handleEdit?: any;
}) {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const [isdelete, setIsDelete] = React.useState(false);
  const [isCloning, setIsCloning] = React.useState(false);
  const navigate = useNavigate(); // Use the navigate hook

  const { toast } = useToast();
  const [deleteWorkout, { isLoading: isdeletingWorkout }] =
    useDeleteWorkoutMutation();
  const [addWorkout] = useAddWorkoutMutation();
  const [addWorkoutDay] = useAddWorkoutDayMutation();
  const [addExerciseInWorkout] = useAddExerciseInWorkoutMutation();
  const [fetchWorkoutById] = useLazyGetWorkoutByIdQuery();
  const [verifyWorkout, { isLoading: isVerifyLoading }] = useVerifyWorkoutCreationMutation();
  const [updateWorkout] = useUpdateWorkoutMutation();
  const [updateWorkoutStatus, { isLoading: isUpdatingStatus }] = useUpdateWorkoutStatusMutation();
  const [isSavingDraft, setIsSavingDraft] = React.useState(false);
  const deleteRow = async () => {
    try {
      const resp = await deleteWorkout({
        workoutid: data.id,
        org_id: orgId,
      }).unwrap();
      if (resp) {
        refetch();
        toast({
          variant: "success",
          title: "Deleted Successfully",
        });
      }
      return;
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail || (typedError.data as { message?: string }).message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };
  const handleEditClick = (data: WorkoutPlanView & { id: number }) => {
    // Navigate to the edit route with the specific ID
    navigate(`/admin/workoutplans/add/step/1/${data.id}?mode=edit`);
  };

  const handleClone = async (data: WorkoutPlanView & { id: number }) => {
    if (isCloning) return;
    setIsCloning(true);
    try {
      // Step 1: Create the new workout (metadata only)
      const payload = {
        workout_name: `${data.workout_name} (copy)`,
        org_id: orgId,
        description: data.description || "",
        visible_for: data.visible_for as string | undefined,
        goals: data.goals,
        level: data.level,
        weeks: data.weeks,
        img_url: data.image_url,
        members: data.members?.map((m: { member_id: number }) => m.member_id) ?? [],
        status: "clone" as const,
        parent_workout_id: data.id,
      };
      const newWorkout = await addWorkout(payload).unwrap();
      if (!newWorkout?.id) {
        throw new Error("Failed to create cloned workout");
      }
      const newWorkoutId: number = newWorkout.id;

      // Step 2: Fetch the original workout with all days & exercises
      const original = await fetchWorkoutById({
        org_id: orgId,
        workoutId: String(data.id),
        include_days: true,
        include_days_and_exercises: true,
      }).unwrap();

      // Step 3: Re-create each day and its exercises in the new workout
      if (original?.days?.length) {
        await Promise.all(
          original.days.map(async (day) => {
            const newDay = await addWorkoutDay({
              workout_id: newWorkoutId,
              day_name: day.day_name,
              week: day.week,
              day: day.day,
            }).unwrap();

            if (newDay?.id && day.exercises?.length) {
              await Promise.all(
                day.exercises.map((ex) =>
                  addExerciseInWorkout({
                    workout_day_id: newDay.id,
                    exercise_id: ex.exercise_id,
                    exercise_type: ex.exercise_type as any,
                    sets: ex.sets ?? undefined,
                    seconds_per_set: ex.seconds_per_set ?? [],
                    repetitions_per_set: ex.repetitions_per_set ?? [],
                    rest_between_set: ex.rest_between_set ?? [],
                    exercise_intensity: ex.exercise_intensity as any ?? undefined,
                    intensity_value: ex.intensity_value ?? undefined,
                    notes: ex.notes ?? "",
                    distance: ex.distance ?? undefined,
                    speed: ex.speed ?? undefined,
                    met_id: ex.met_id ?? undefined,
                  }).unwrap()
                )
              );
            }
          })
        );
      }

      refetch?.();
      toast({
        variant: "success",
        title: "Workout cloned successfully",
        description: "Days and exercises have been copied.",
      });
    } catch (error) {
      console.error("Clone error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error cloning workout",
          description: `${typedError.data?.detail || (typedError.data as { message?: string }).message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error cloning workout",
          description: "Something went wrong.",
        });
      }
    } finally {
      setIsCloning(false);
    }
  };

  const handleSaveAsDraft = async (data: WorkoutPlanView & { id: number }) => {
  if (isSavingDraft) return;
  setIsSavingDraft(true);
  try {
    await updateWorkoutStatus({
      id: data.id,
      org_id: orgId,
      status: "draft",
    }).unwrap();

    refetch?.();
    toast({ variant: "success", title: "Saved as draft" });
  } catch (error) {
    console.error("Save as draft error", { error });
    if (error && typeof error === "object" && "data" in error) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error saving as draft",
        description: `${typedError.data?.detail || (typedError.data as { message?: string }).message}`,
      });
    } else {
      toast({
        variant: "destructive",
        title: "Error saving as draft",
        description: "Something went wrong.",
      });
    }
  } finally {
    setIsSavingDraft(false);
  }
};

const handleSaveAsPublished = async (data: WorkoutPlanView & { id: number }) => {
  try {
    await updateWorkoutStatus({
      id: data.id,
      org_id: orgId,
      status: "published",
    }).unwrap();

    refetch?.();
    toast({ variant: "success", title: "Published successfully" });
  } catch (error) {
    console.error("Publish error", { error });
    if (error && typeof error === "object" && "data" in error) {
      const typedError = error as ErrorType;
      const displayMessage =
        typedError.data?.message ||
        typedError.data?.detail ||
        "An error occurred while publishing. Please try again.";
      toast({ variant: "destructive", description: displayMessage });
    } else {
      toast({
        variant: "destructive",
        description: "An error occurred while publishing. Please try again.",
      });
    }
  }
};

const handleArchive = async (data: WorkoutPlanView & { id: number }) => {
  try {
    await updateWorkoutStatus({
      id: data.id,
      org_id: orgId,
      status: "archived",
    }).unwrap();

    refetch?.();
    toast({ variant: "success", title: "Workout archived" });
  } catch (error) {
    console.error("Archive error", { error });
    if (error && typeof error === "object" && "data" in error) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error archiving workout",
        description: `${typedError.data?.detail || (typedError.data as { message?: string }).message}`,
      });
    } else {
      toast({
        variant: "destructive",
        title: "Error archiving workout",
        description: "Something went wrong.",
      });
    }
  }
};

  return (
    <>
      <Dialog>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
            >
              <MoreVertical className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-4" onClick={(e) => e.stopPropagation()}>
            <DialogTrigger asChild>
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                handleEditClick(data)
              }}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
            </DialogTrigger>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleClone(data);
              }}
              disabled={isCloning}
            >
              {isCloning ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Copy className="mr-2 h-4 w-4" />
              )}
              {isCloning ? "Cloning..." : "Clone"}
            </DropdownMenuItem>
            {[
                    { status: "published", label: "Save as Published", icon: CheckCircle2, loading: isUpdatingStatus, action: handleSaveAsPublished },
                    { status: "draft", label: "Save as Draft", icon: FileEdit, loading: isUpdatingStatus, action: handleSaveAsDraft },
                    { status: "archived", label: "Archive", icon: Archive, loading: isUpdatingStatus, action: handleArchive },
                  ].map(
                    (item) =>
                      data.status !== item.status && (
                        <DropdownMenuItem
                          key={item.status}
                          onClick={(e) => {
                            e.stopPropagation();
                            item.action(data);
                          }}
                          disabled={item.loading}
                        >
                          {item.loading ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <item.icon className="mr-2 h-4 w-4" />
                          )}
                          {item.label}
                        </DropdownMenuItem>
                      )
                )}
            {access === "full_access" && (
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                setIsDelete(true)
              }}>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </Dialog>
      {isdelete && (
        <AlertDialog open={isdelete} onOpenChange={setIsDelete}>
          <AlertDialogContent onClick={(e) => e.stopPropagation()}>
            <AlertDialogHeader>
              <AlertDialogDescription>
                <div className="flex flex-col items-center  justify-center gap-4">
                  <img src={warning} alt="warning" className="w-18 h-18" />
                  <AlertDialogTitle className="text-xl font-semibold w-80 text-center">
                    Please confirm if you want to delete this workout
                  </AlertDialogTitle>
                </div>
                <div className="w-full flex justify-between items-center gap-3 mt-4">
                  <AlertDialogCancel
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDelete(false)
                    }}
                    className="w-full border border-primary font-semibold"
                  >
                    <i className="fa fa-xmark text-base px-1 "></i>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteRow()
                    }}
                    className="w-full bg-primary !text-black font-semibold"
                  >
                    <i className="fa-regular fa-floppy-disk text-base px-1 "></i>
                    Delete
                  </AlertDialogAction>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
